// Shared plumbing for collection-size-filter.js and collection-availability-filter.js, exposed as window.CollectionFilterShared.
;(() => {
  const MAX_BACKGROUND_PAGES = 40

  // Keyed by product list, so the original and sticky-copy filters share one load and an ajax-swapped grid loads afresh.
  const loadPromises = new WeakMap()

  function buildPageUrl(sectionId, page) {
    const url = new URL(window.location.href)
    url.searchParams.set('page', page)
    url.searchParams.set('section_id', sectionId)
    return url.pathname + '?' + url.searchParams.toString()
  }

  function loadAllPages(sectionId, productList) {
    if (!loadPromises.has(productList)) loadPromises.set(productList, fetchAndMergePages(sectionId, productList))
    return loadPromises.get(productList)
  }

  function fetchAndMergePages(sectionId, productList) {
    const currentPage = parseInt(productList.dataset.currentPage, 10) || 1
    const totalPages = Math.min(parseInt(productList.dataset.totalPages, 10) || 1, MAX_BACKGROUND_PAGES)

    if (totalPages <= 1) return Promise.resolve()

    const pagesToFetch = []
    for (let page = 1; page <= totalPages; page++) {
      if (page !== currentPage) pagesToFetch.push(page)
    }

    return Promise.all(
      pagesToFetch.map((page) =>
        fetch(buildPageUrl(sectionId, page))
          .then((response) => response.text())
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html')
            const otherList = doc.querySelector('.product-list')
            // Keep every child, not just .product-block, so grid-promotion-block's <style> tag survives.
            return { page, cards: otherList ? Array.from(otherList.childNodes) : [] }
          })
          .catch(() => ({ page, cards: [] }))
      )
    ).then((results) => {
      results.sort((a, b) => a.page - b.page)

      const existingCards = Array.from(productList.childNodes)
      const fragment = document.createDocumentFragment()

      // Strip data-cc-animate so scroll-reveal doesn't get stuck hidden.
      const stripAnimate = (node) => {
        if (node.nodeType === 1) node.removeAttribute('data-cc-animate')
        fragment.appendChild(node)
      }

      results.forEach(({ page, cards }) => {
        if (page < currentPage) cards.forEach(stripAnimate)
      })
      existingCards.forEach((card) => fragment.appendChild(card))
      results.forEach(({ page, cards }) => {
        if (page > currentPage) cards.forEach(stripAnimate)
      })

      productList.innerHTML = ''
      productList.appendChild(fragment)

      if (window.theme && window.theme.ProductBlockManager && window.jQuery) {
        window.theme.ProductBlockManager.loadImages(window.jQuery(productList))
      }
    })
  }

  // Promo blocks must be tagged before a filter first repositions them, or they'd be tagged at their moved position
  // (an untagged promo moves to the top). Tagging before the other pages load is only right on page 1, since loading
  // prepends earlier pages and would shift the counts.
  function isFirstPage(productList) {
    return (parseInt(productList.dataset.currentPage, 10) || 1) === 1
  }

  // Tags each promo block with how many real cards preceded it, so it can be re-anchored later.
  function tagPromoPositions(productList, afterAttr) {
    let realCount = 0
    productList.querySelectorAll('.product-block').forEach((card) => {
      if (card.hasAttribute('data-product-id')) {
        realCount++
      } else if (card.dataset[afterAttr] === undefined) {
        card.dataset[afterAttr] = String(realCount)
      }
    })
  }

  function repositionPromoBlocks(productList, hiddenClass, afterAttr) {
    productList.querySelectorAll('.product-block:not([data-product-id])').forEach((promo) => {
      const after = parseInt(promo.dataset[afterAttr] || '0', 10)
      const styleTag = promo.nextElementSibling && promo.nextElementSibling.tagName === 'STYLE' ? promo.nextElementSibling : null
      const visibleCards = Array.from(productList.querySelectorAll(`.product-block[data-product-id]:not(.${hiddenClass})`))

      if (after <= 0 || visibleCards.length === 0) {
        productList.insertBefore(promo, productList.firstChild)
      } else {
        const anchor = visibleCards[Math.min(after, visibleCards.length) - 1]
        anchor.after(promo)
      }
      if (styleTag) promo.after(styleTag)
    })
  }

  // Hides a promo block if no visible real card separates it from the previous one.
  function hideOrphanedPromoBlocks(productList, hasActive, hiddenClass) {
    const relevant = Array.from(productList.children).filter((el) => el.classList && el.classList.contains('product-block'))
    let sawVisiblePromo = false
    relevant.forEach((el) => {
      const isPromo = !el.hasAttribute('data-product-id')
      if (!isPromo) {
        if (!el.classList.contains(hiddenClass)) sawVisiblePromo = false
        return
      }
      const shouldHide = hasActive && sawVisiblePromo
      el.classList.toggle(hiddenClass, shouldHide)
      if (!shouldHide) sawVisiblePromo = true
    })
  }

  // Clones a sample of hidden products into the "made to order" section below the grid.
  function updateFilteredOutSamples(productList, hiddenClass, maxSamples) {
    const root = productList.closest('[data-section-type="collection-template"]')
    const section = root && root.querySelector('[data-filtered-out-products]')
    if (!section) return
    const grid = section.querySelector('[data-filtered-out-products-grid]')
    if (!grid) return

    const hiddenCards = Array.from(
      productList.querySelectorAll(`.product-block[data-product-id].${hiddenClass}`)
    ).slice(0, maxSamples)

    grid.innerHTML = ''
    section.hidden = hiddenCards.length === 0
    if (hiddenCards.length === 0) return

    const fragment = document.createDocumentFragment()
    hiddenCards.forEach((card) => {
      const clone = card.cloneNode(true)
      clone.classList.remove(hiddenClass)
      clone.removeAttribute('data-cc-animate')
      fragment.appendChild(clone)
    })
    grid.appendChild(fragment)

    if (window.theme && window.theme.ProductBlockManager && window.jQuery) {
      window.theme.ProductBlockManager.loadImages(window.jQuery(grid))
    }
  }

  // Holds the grid in theme.js's ajax loading state while a filter reapplies after an ajax swap.
  function setGridLoading(productList, isLoading) {
    const container = productList.closest('[data-ajax-container]')
    if (container) container.classList.toggle('ajax-loading', isLoading)
  }

  // Swaps a filter button's label between its default and active text.
  function updateButtonLabel(filterEl, hasActive, textSelector) {
    const textEl = filterEl.querySelector(textSelector)
    if (!textEl) return
    if (hasActive) {
      if (!textEl.dataset.defaultText) textEl.dataset.defaultText = textEl.textContent
      textEl.textContent = filterEl.dataset.labelActive || textEl.textContent
    } else if (textEl.dataset.defaultText) {
      textEl.textContent = textEl.dataset.defaultText
    }
  }

  window.CollectionFilterShared = {
    buildPageUrl,
    loadAllPages,
    tagPromoPositions,
    isFirstPage,
    repositionPromoBlocks,
    hideOrphanedPromoBlocks,
    updateFilteredOutSamples,
    updateButtonLabel,
    setGridLoading,
  }
})()
